'use client';

import { Button } from '@/components/ui/button';
import { useQrCodeGeneratorStore } from '@/components/provider/QrCodeConfigStoreProvider';
import { useCreateQrCodeMutation } from '@/lib/api/qr-code';
import { useCreateQrExperienceMutation } from '@/lib/api/qr-experience';
import type { TQrExperienceContent } from '@shared/schemas';
import { toast } from '@/components/ui/use-toast';
import { useAuth, useClerk } from '@/auth/client';
import { safeLocalStorage } from '@/lib/utils';
import { SavedQrCodeDownloadBtn } from '@/components/qr-generator/download-buttons';
import type { TQrCodeWithRelationsResponseDto } from '@shared/schemas';
import { useState } from 'react';
import { NameDialog } from '@/components/qr-generator/NameDialog';
import { getSuggestedQrName, isRequiredQrNameValid } from './publish-helpers';

export function PublishExperienceButton({ content }: { content: TQrExperienceContent }) {
	const { config, name, updateName } = useQrCodeGeneratorStore((state) => state);
	const { isLoaded, isSignedIn } = useAuth();
	const { openSignIn } = useClerk();
	const createQr = useCreateQrCodeMutation();
	const createExperience = useCreateQrExperienceMutation();
	const [publishedQrCode, setPublishedQrCode] = useState<TQrCodeWithRelationsResponseDto | null>(
		null,
	);
	const [nameDialogOpen, setNameDialogOpen] = useState(false);
	const publish = async (qrCodeName: string) => {
		if (!isLoaded) return;
		if (!isRequiredQrNameValid(qrCodeName)) return;
		setNameDialogOpen(false);
		updateName(qrCodeName);
		if (!isSignedIn) {
			safeLocalStorage.setItem(
				'guidedQrDraft',
				JSON.stringify({
					version: 2,
					experience: content.type,
					name: qrCodeName,
					content,
					savedAt: Date.now(),
				}),
			);
			safeLocalStorage.setItem('unsavedQrConfig', JSON.stringify(config));
			openSignIn({ forceRedirectUrl: '/create?resumeDraft=1' });
			return;
		}
		try {
			const qrCode = await createQr.mutateAsync({
				name: qrCodeName,
				config,
				content: {
					type: 'url',
					data: { url: `${window.location.origin}/p/pending`, isDynamic: true },
				},
			});
			if (!qrCode.shortUrl) throw new Error('Dynamic QR code was not created');
			await createExperience.mutateAsync({
				qrCodeId: qrCode.id,
				shortUrlId: qrCode.shortUrl.id,
				content,
			});
			safeLocalStorage.removeItem('guidedQrDraft');
			setPublishedQrCode(qrCode);
			toast({
				title: 'QR code published',
				description: 'Your dynamic QR code is saved and ready to download.',
			});
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Could not publish QR code',
				description: error instanceof Error ? error.message : 'Please try again.',
			});
		}
	};
	if (publishedQrCode) return <SavedQrCodeDownloadBtn qrCode={publishedQrCode} />;
	return (
		<>
			<Button
				type="button"
				onClick={() => setNameDialogOpen(true)}
				disabled={!isLoaded}
				isLoading={createQr.isPending || createExperience.isPending}
			>
				{isSignedIn ? 'Publish QR code' : 'Sign in to publish'}
			</Button>
			<NameDialog
				dialogHeadline="Name your QR code"
				placeholder="QR code name"
				defaultValue={getSuggestedQrName(content, name)}
				required
				isOpen={nameDialogOpen}
				setIsOpen={setNameDialogOpen}
				onSubmit={(qrCodeName) => void publish(qrCodeName)}
			/>
		</>
	);
}
